/**
 * ShippingFeesAdmin.tsx — Admin > Shipping Fees.
 *
 * The corporate dashboard's Shipping Fees page for the signed-in account, in
 * the Lead Sources layout (see components/admin/AdminList.tsx):
 *   - Name and Amount ($0.00), in corp's order (fee order, then name), either
 *     column sorting from its title; search by name; Show deleted;
 *   - "Add shipping fee" top right; a click on a live row opens it; Edit /
 *     Delete on a live row, Restore on a deleted one;
 *   - corp's form: the "charged once per payment" note, Name * and Amount ($) *,
 *     with corp's suggestions ($0 is never charged; a new amount only applies
 *     to new payments).
 * Deleting a fee takes it off every product carrying it; restoring does not
 * put it back on them — the prompt and the messages say so, as corp's do.
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live fee still opens, read-only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareNumber, compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { getErrorMessage } from '@/services/apiClient';
import {
  createShippingFee,
  deleteShippingFee,
  listAdminShippingFees,
  restoreShippingFee,
  updateShippingFee,
  type AdminShippingFee,
} from '@/services/shippingFees.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Name', sortable: true },
  { key: 'amount', header: 'Amount', sortable: true, className: 'w-32' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

const SORT_COMPARE: ListSortCompare<AdminShippingFee> = {
  name: (a, b) => compareText(a.name, b.name),
  amount: (a, b) => compareNumber(a.amount, b.amount),
};

export function formatFeeAmount(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

function feeName(f: AdminShippingFee): string {
  return f.name.trim() || `Shipping fee #${f.shippingFeeID}`;
}

export function ShippingFeesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [fees, setFees] = useState<AdminShippingFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminShippingFee | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminShippingFee | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setFees(await listAdminShippingFees());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load shipping fees'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  // The API's order (fee order, then name) until a title is clicked.
  const rows = useMemo(
    () => fees.filter((f) => (showDeleted || f.active) && (query === '' || f.name.toLowerCase().includes(query))),
    [fees, showDeleted, query],
  );
  const sorting = useListSort(rows, { initialKey: null, compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const { shippingFeeID } = deleting;
    const name = feeName(deleting);
    try {
      await deleteShippingFee(shippingFeeID);
      setFees((prev) => prev.map((f) => (f.shippingFeeID === shippingFeeID ? { ...f, active: false } : f)));
      toast.success(`"${name}" removed from any product carrying it. Payments already charged keep their shipping.`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The shipping fee could not be deleted.'));
    }
  }

  async function handleRestore(f: AdminShippingFee) {
    try {
      await restoreShippingFee(f.shippingFeeID);
      setFees((prev) => prev.map((x) => (x.shippingFeeID === f.shippingFeeID ? { ...x, active: true } : x)));
      toast.success(`"${feeName(f)}" restored. Re-assign it to products as needed.`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The shipping fee could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="Shipping Fees"
      subtitle="Flat fees charged once per payment when a product that carries them is purchased."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add shipping fee
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search shipping fees..."
          searchLabel="Search shipping fees"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="shipping-fees-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading shipping fees…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No shipping fees found' : 'No shipping fees yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a shipping fee to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Shipping fees" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((f) => {
              const deleted = !f.active;
              const name = feeName(f);
              return (
                <tr
                  key={f.shippingFeeID}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(f)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="shipping-fee-name">
                      {name}
                    </AdminRowName>
                  </td>
                  <td className={`${ADMIN_TD_CLASS} tabular-nums`}>{formatFeeAmount(f.amount)}</td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(f)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setEditing(f)} />
                            <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(f)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['shipping fee', 'shipping fees']} />
        </>
      )}

      {editing && (
        <ShippingFeeFormModal
          key={editing === 'new' ? 'new' : editing.shippingFeeID}
          fee={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${feeName(deleting)}"?` : ''}
        message="It will be removed from any product carrying it; payments already charged keep their shipping. You can restore it from Show deleted, then re-assign it to products."
        confirmLabel="Delete shipping fee"
        variant="danger"
      />
    </AdminListPage>
  );
}

function ShippingFeeFormModal({
  fee,
  onClose,
  onSaved,
}: {
  fee: AdminShippingFee | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = fee === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm(() => ({ name: fee?.name ?? '', amount: fee ? fee.amount.toFixed(2) : '' }), { tracking: !isNew });
  const [saving, setSaving] = useState(false);

  const amount = t.form.amount.trim() === '' ? NaN : Number(t.form.amount);
  const missingName = t.form.name.trim() === '';
  const badAmount = !Number.isFinite(amount) || amount < 0;
  const errors = [...(missingName ? ['Name is required'] : []), ...(badAmount ? ['Amount must be zero or greater'] : [])];
  const suggestions = [
    ...(!badAmount && amount === 0 ? ['Amount is $0 — this fee will never be charged on a payment'] : []),
    ...(!isNew && !badAmount && fee && Math.abs(amount - fee.amount) > 0.0001
      ? ['Payments already charged keep the amount they were charged; only new payments use the new one']
      : []),
  ];

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    try {
      const data = { name: t.form.name.trim(), amount: Math.round(amount * 100) / 100 };
      if (fee) await updateShippingFee(fee.shippingFeeID, data);
      else await createShippingFee(data);
      toast.success(isNew ? 'Shipping fee created' : 'Shipping fee updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The shipping fee could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Shipping Fee' : 'Edit Shipping Fee'}
      isNew={isNew}
      dirty={isNew ? t.form.name !== '' || t.form.amount !== '' : t.dirty}
      saving={saving}
      errors={errors}
      suggestions={suggestions}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="md"
    >
      <div className="grid gap-4">
        {locked ? (
          <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
            It is charged <strong>once per payment</strong> however many products carrying it are bought, and is never taxed
            or discounted.
          </p>
        ) : (
          <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
            Assign a fee to products on the Products screen. It is charged <strong>once per payment</strong> however many of
            those products are bought, and is never taxed or discounted.
          </p>
        )}
        <DialogField
          label="Name"
          htmlFor="shipping-fee-name"
          required
          state={missingName ? 'error' : 'none'}
          labelHint={missingName ? '— required' : undefined}
          badge={t.badge('name', 'Name')}
        >
          <Input
            id="shipping-fee-name"
            value={t.form.name}
            placeholder="e.g. Peptides"
            maxLength={100}
            onChange={(e) => t.patch({ name: e.target.value })}
            className={fieldClasses.input(missingName ? 'error' : 'none')}
          />
        </DialogField>
        <DialogField
          label="Amount ($)"
          htmlFor="shipping-fee-amount"
          required
          state={badAmount ? 'error' : 'none'}
          labelHint={badAmount ? '— required' : undefined}
          badge={t.badge('amount', 'Amount')}
        >
          <Input
            id="shipping-fee-amount"
            type="number"
            step="0.01"
            min="0"
            value={t.form.amount}
            placeholder="25.00"
            onChange={(e) => t.patch({ amount: e.target.value })}
            className={fieldClasses.input(badAmount ? 'error' : 'none')}
          />
        </DialogField>
      </div>
    </AdminFormModal>
  );
}
